import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Products from "@/pages/Products";
import Inventory from "@/pages/Inventory";
import GoldRates from "@/pages/GoldRates";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Schemes from "@/pages/Schemes";
import Agents from "@/pages/Agents";
import Discounts from "@/pages/Discounts";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import Roles from "@/pages/Roles";
import Branches from "@/pages/Branches";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import Portal from "@/pages/Portal";
import AgentPortal from "@/pages/AgentPortal";
import Expenses from "@/pages/Expenses";
import Purchases from "@/pages/Purchases";
import WhatsAppOffers from "@/pages/WhatsAppOffers";

function isCustomer(user) {
  return user && (user.role_name === "Customer" || (!!user.customer_id && (user.permissions || []).includes("portal:view")));
}

function isAgentUser(user) {
  return user && (user.role_name === "Agent" || !!user.agent_id);
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center text-[#C5A059] font-display text-2xl">Loading OM GOLD…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (isCustomer(user)) return <Navigate to="/portal" replace />;
  if (isAgentUser(user)) return <Navigate to="/agent" replace />;
  return <Layout>{children}</Layout>;
}

function PortalProtected() {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center text-[#C5A059] font-display text-2xl">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <Portal />;
}

function AgentPortalProtected() {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center text-[#C5A059] font-display text-2xl">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!isAgentUser(user)) return <Navigate to="/" replace />;
  return <AgentPortal />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/portal" element={<PortalProtected />} />
      <Route path="/agent" element={<AgentPortalProtected />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/pos" element={<Protected><POS /></Protected>} />
      <Route path="/products" element={<Protected><Products /></Protected>} />
      <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
      <Route path="/gold-rates" element={<Protected><GoldRates /></Protected>} />
      <Route path="/customers" element={<Protected><Customers /></Protected>} />
      <Route path="/customers/:id" element={<Protected><CustomerDetail /></Protected>} />
      <Route path="/schemes" element={<Protected><Schemes /></Protected>} />
      <Route path="/agents" element={<Protected><Agents /></Protected>} />
      <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
      <Route path="/purchases" element={<Protected><Purchases /></Protected>} />
      <Route path="/whatsapp" element={<Protected><WhatsAppOffers /></Protected>} />
      <Route path="/discounts" element={<Protected><Discounts /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/roles" element={<Protected><Roles /></Protected>} />
      <Route path="/branches" element={<Protected><Branches /></Protected>} />
      <Route path="/audit" element={<Protected><Audit /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
